import React, { useContext, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { Scope } from '@/contexts/ScopeContext';
import { useSession } from '@/state/Session';
import { unpackSessionObject } from '@/components/pages/unpackSessionObject';
import { sendStaffSms, StaffSmsPreview, StaffSmsResult } from './preOpBoardStaffSms';

const useStyles = makeStyles(theme => ({
  message: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    backgroundColor: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
  group: { marginTop: theme.spacing(2), fontWeight: 600 },
  recipient: { paddingLeft: 0, paddingRight: 0, height: 64 },
  recipientText: { minWidth: 0 },
  enrolledList: {
    maxHeight: 5 * 64,
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    '@media (max-height: 900px)': { maxHeight: '30vh' },
  },
  paper: { margin: theme.spacing(2), width: `calc(100% - ${theme.spacing(4)}px)` },
  content: { [theme.breakpoints.down('xs')]: { padding: theme.spacing(1.5) } },
  actions: {
    padding: theme.spacing(2),
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    '& > :not(:first-child)': { marginLeft: 0 },
  },
}));

export default function PreOpBoardStaffSmsDialog({
  patientId,
  patientName,
  preview,
  onClose,
}: {
  patientId: string | number;
  patientName: string;
  preview: StaffSmsPreview;
  onClose: () => void;
}) {
  const classes = useStyles();
  const scope = useContext(Scope);
  const sessionContext = useSession();
  const { isSuperAdmin, isGroupAdmin } = unpackSessionObject(sessionContext || {});
  const configurationUrl = `${(isSuperAdmin || isGroupAdmin) && scope?.hospital?.id ? `/su/${scope.hospital.id}` : ''}/configuration?tab=staff#s-ready-enrollment`;
  const groupLabels = { ENROLLED: 'S-Ready enrolled staff', PROCEDURE: 'Procedure staff', OR: 'OR staff' };
  const [selected, setSelected] = useState(() => preview.recipients.filter(r => r.phoneNumber).map(r => r.id));
  const [send, { loading }] = useMutation<{ results: StaffSmsResult[] }>(sendStaffSms);
  const submitted = useRef(false);
  const [finished, setFinished] = useState(false);
  const [results, setResults] = useState<StaffSmsResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const accepted = results.filter(r => r.accepted).length;
  const eligible = preview.recipients.filter(r => r.phoneNumber).length;

  const submit = async () => {
    if (submitted.current || !selected.length) return;
    submitted.current = true;
    try {
      const response = await send({
        variables: {
          patientId,
          procedureId: preview.procedureId,
          recipientIds: selected,
          expectedMessage: preview.message,
        },
      });
      if (!response.data?.results) throw new Error('Missing SMS result');
      setResults(response.data.results);
    } catch (_error) {
      setError('SMS sending could not be confirmed. S-Ready is saved. Check the message log before sending again.');
    } finally {
      setFinished(true);
    }
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      onClose={loading ? undefined : onClose}
      disableEscapeKeyDown={loading}
      classes={{ paper: classes.paper }}
      aria-labelledby="preop-staff-sms-title"
    >
      <DialogTitle id="preop-staff-sms-title">Notify staff — S-Ready</DialogTitle>
      <DialogContent dividers className={classes.content}>
        <Typography variant="h6">{patientName}</Typography>
        <Typography color="textSecondary">{preview.procedureName}</Typography>
        <Box mt={2}>
          <Typography variant="subtitle2">Message preview</Typography>
          <Typography className={classes.message}>{preview.message}</Typography>
        </Box>
        {!finished && (
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              Select who should receive this SMS.
            </Typography>
          </Box>
        )}
        {(['ENROLLED', 'PROCEDURE', 'OR'] as const).map(source => {
          const recipients = preview.recipients.filter(r => r.source === source);
          return (
            (source === 'ENROLLED' || recipients.length > 0) && (
              <React.Fragment key={source}>
                <Typography className={classes.group} variant="subtitle2">
                  {groupLabels[source]}
                </Typography>
                {source === 'ENROLLED' && (
                  <Typography variant="body2" color="textSecondary">
                    {recipients.length
                      ? 'Enrolled through Messaging Configuration.'
                      : 'No staff are enrolled for S-Ready notifications.'}
                    {!recipients.length && (
                      <>
                        {' '}
                        <Link href={configurationUrl} target="_blank" rel="noopener noreferrer">
                          Manage S-Ready enrollment
                        </Link>
                      </>
                    )}
                  </Typography>
                )}
                <List
                  dense
                  disablePadding
                  aria-label={groupLabels[source]}
                  className={source === 'ENROLLED' ? classes.enrolledList : undefined}
                  tabIndex={source === 'ENROLLED' && recipients.length ? 0 : undefined}
                >
                  {recipients.map(recipient => {
                    const result = results.find(r => r.recipientId === recipient.id);
                    return (
                      <ListItem key={recipient.id} className={classes.recipient}>
                        <ListItemIcon>
                          <Checkbox
                            checked={selected.includes(recipient.id)}
                            color="primary"
                            disabled={!recipient.phoneNumber || loading || finished}
                            inputProps={{ 'aria-label': `Send SMS to ${recipient.name}` }}
                            onChange={(_event, checked) =>
                              setSelected(current =>
                                checked ? [...current, recipient.id] : current.filter(id => id !== recipient.id)
                              )
                            }
                          />
                        </ListItemIcon>
                        <ListItemText
                          className={classes.recipientText}
                          primary={recipient.name}
                          secondary={recipient.phoneNumber || 'No phone number'}
                          primaryTypographyProps={{ noWrap: true, title: recipient.name }}
                          secondaryTypographyProps={{ noWrap: true, title: recipient.phoneNumber || 'No phone number' }}
                        />
                        {result && (
                          <Chip
                            size="small"
                            label={result.accepted ? 'Accepted' : 'Not confirmed'}
                            color={result.accepted ? 'primary' : 'default'}
                          />
                        )}
                      </ListItem>
                    );
                  })}
                </List>
              </React.Fragment>
            )
          );
        })}
        {!eligible && (
          <Box mt={2}>
            <Alert severity="info">
              {preview.recipients.length
                ? 'No available recipients have a phone number.'
                : 'No staff are enrolled or assigned to this procedure or its OR at the scheduled time.'}
            </Alert>
          </Box>
        )}
        {finished && (
          <Box mt={2}>
            <Alert severity={error || !accepted ? 'error' : accepted < selected.length ? 'warning' : 'success'}>
              {error || `${accepted} of ${selected.length} recipients accepted by the SMS service. S-Ready is saved.`}
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        {!finished && (
          <Box flex={1}>
            <Typography variant="body2" color="textSecondary">
              {selected.length} selected
            </Typography>
          </Box>
        )}
        <Button onClick={onClose} disabled={loading}>
          {finished ? 'Done' : 'Cancel'}
        </Button>
        {!finished && (
          <Button
            variant="contained"
            color="primary"
            disabled={loading || !selected.length}
            onClick={submit}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {loading ? 'Sending…' : 'Send SMS'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
